import { useCallback, useEffect, useMemo, useState } from 'react';

const STORAGE_PREFIX = 'flotte.tableColumns.';

const clampWidth = (value, min = 120, max = 420) => {
  if (Number.isNaN(value)) return min;
  return Math.min(Math.max(value, min), max);
};

export default function useTableColumns(tableId, defaults = []) {
  const storageKey = `${STORAGE_PREFIX}${tableId}`;

  const readFromStorage = useCallback(() => {
    if (typeof window === 'undefined') return null;
    try {
      const raw = window.localStorage.getItem(storageKey);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        return parsed;
      }
    } catch {
      return null;
    }
    return null;
  }, [storageKey]);

  const [columns, setColumns] = useState(() => {
    const stored = readFromStorage();
    if (Array.isArray(stored) && stored.length) {
      return stored;
    }
    return defaults.map(column => ({
      ...column,
      width: clampWidth(Number(column.width) || 180),
      visible: column.visible !== false,
    }));
  });

  useEffect(() => {
    setColumns(prev => {
      const stored = readFromStorage();
      if (stored) return stored;
      const merged = defaults.map(column => {
        const prevColumn = prev.find(item => item.key === column.key);
        return {
          ...column,
          width: clampWidth(
            Number(prevColumn?.width ?? column.width) || 180,
            column.minWidth,
            column.maxWidth
          ),
          visible: prevColumn?.visible ?? column.visible !== false,
        };
      });
      return merged;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaults]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(storageKey, JSON.stringify(columns));
    } catch {
      // ignore write errors
    }
  }, [columns, storageKey]);

  const moveColumn = useCallback((fromIndex, toIndex) => {
    setColumns(prev => {
      if (fromIndex === toIndex) return prev;
      const next = [...prev];
      const [moved] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, moved);
      return next;
    });
  }, []);

  const toggleColumn = useCallback(key => {
    setColumns(prev =>
      prev.map(column =>
        column.key === key ? { ...column, visible: !column.visible } : column
      )
    );
  }, []);

  const setColumnWidth = useCallback((key, width) => {
    setColumns(prev =>
      prev.map(column =>
        column.key === key
          ? {
              ...column,
              width: clampWidth(width, column.minWidth, column.maxWidth),
            }
          : column
      )
    );
  }, []);

  const resetColumns = useCallback(() => {
    setColumns(
      defaults.map(column => ({
        ...column,
        width: clampWidth(Number(column.width) || 180, column.minWidth, column.maxWidth),
        visible: column.visible !== false,
      }))
    );
    if (typeof window !== 'undefined') {
      try {
        window.localStorage.removeItem(storageKey);
      } catch {
        // ignore
      }
    }
  }, [defaults, storageKey]);

  const visibleColumns = useMemo(
    () => columns.filter(column => column.visible !== false),
    [columns]
  );

  return {
    columns,
    visibleColumns,
    moveColumn,
    toggleColumn,
    setColumnWidth,
    resetColumns,
  };
}

